import React, { useContext, useEffect, useRef, useState } from 'react';
import proptypes from 'prop-types';
import { Button, RatingMeter, Skeleton, SkeletonBlock } from '@one-thd/sui-atomic-components';
import { ArrowDown } from '@one-thd/sui-icons';
import { useDataModel } from '@thd-nucleus/data-sources';
import { getProductObject, getReviewInfo, smoothScroll } from '../../productDetailsUtils';
import * as analytics from '../../analytics';
import { ProductDetailsContext } from '../ProductDetailsContext';
import { dataModel } from './ratings-and-reviews-data-model';
import { RatingsPopover } from './RatingsPopover/RatingsPopover.component';

const { bool, func, string } = proptypes;

const ProductDetailsRatings = (
  {
    offsetselector = null, qaselector = null, pageType = 'PIP', target = null, hideRatingsIfHDPPSku = false, targetURL = null, showBadge = false,
    disableReviewsIfZero = false, onClick = () => { }, useHeaderIndex = false, hideRatings = false, hideWhenNoQA = false
  }
) => {
  const { product, itemId, channel } = useContext(ProductDetailsContext);

  const [showPopover, setShowPopover] = useState(false);
  const ratingsRef = useRef(null);
  const hoverCloseTimerRef = useRef(null);

  const { data: reviewData, loading: reviewLoading, error: reviewError } = useDataModel('reviews', {
    ssr: !(pageType === 'PEP'),
    variables: {
      itemId,
      filters: {
        isVerifiedPurchase: false,
        prosCons: null,
        starRatings: null
      },
      searchTerm: null,
      sortBy: 'photoreview',
      startIndex: 1
    }
  });

  const { data: qaData, loading: qaLoading, error: qaError } = useDataModel('questionsAnswers', {
    skip: pageType === 'PEP',
    variables: {
      itemId,
      pagesize: 8,
      searchTerm: '',
      sortBy: 'newest',
      startindex: 1
    }
  });

  useEffect(() => {
    return () => {
      if (hoverCloseTimerRef.current) {
        clearTimeout(hoverCloseTimerRef.current);
        hoverCloseTimerRef.current = null;
      }
    };
  }, []);

  const analyticsData = (element) => analytics.track('click', { element });

  if (reviewLoading || !product) {
    return (
      <Skeleton disablePadding>
        <SkeletonBlock height={8} width={32} />
      </Skeleton>
    );
  }

  const openPopover = () => { if (!showPopover) setShowPopover(true); };
  const closePopover = () => { setShowPopover(false); };
  const scheduleClosePopover = () => {
    if (hoverCloseTimerRef.current) clearTimeout(hoverCloseTimerRef.current);
    hoverCloseTimerRef.current = setTimeout(() => {
      setShowPopover(false);
      hoverCloseTimerRef.current = null;
    }, 120);
  };
  const cancelScheduledClose = () => {
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  };
  const handleAnchorBlur = (e) => {
    const next = e?.relatedTarget;
    const popoverRoot = document.querySelector('[data-component="RatingsPopover"]');
    if (next && (ratingsRef.current?.contains(next) || popoverRoot?.contains(next))) return;
    closePopover();
  };
  const handleRatingsClick = (event) => {
    setShowPopover(false);
    if (useHeaderIndex) {
      onClick(event, target, 3);
    } else {
      smoothScroll(event, target, offsetselector);
      onClick(target);
    }
    analyticsData('star rating');
  };
  const handleQuestionAnswerClick = (event) => {
    if (useHeaderIndex) {
      onClick(event, qaselector, 2);
    } else {
      smoothScroll(event, qaselector, offsetselector);
      onClick(qaselector);
    }
    analyticsData('questions and answers');
  };

  const itemObj = getProductObject(reviewData?.reviews, reviewData?.reviews?.TotalResults);
  const { averageRating, ratingsTitle } = getReviewInfo(itemObj, product, reviewData?.reviews?.TotalResults);
  const { skuClassification } = product?.identifiers || {};
  const isHiddenHDPPSku = (hideRatingsIfHDPPSku && skuClassification === 'hdpp');
  const isHiddenZeroReviews = (disableReviewsIfZero && reviewData?.reviews?.TotalResults === 0);
  const isQADataValid = !(qaLoading || qaError) && qaData;

  return (
    <div className="sui-flex sui-flex-row sui-items-center" data-component="ProductDetailsRatings">
      {!isHiddenHDPPSku && !isHiddenZeroReviews && (
        <>
          {!hideRatings && (
            <a
              className="sui-flex sui-flex-row sui-items-center"
              onClick={handleRatingsClick}
              onMouseEnter={() => { cancelScheduledClose(); openPopover(); }}
              onMouseLeave={scheduleClosePopover}
              onFocus={openPopover}
              onBlur={handleAnchorBlur}
              href={target}
              title={ratingsTitle}
              ref={ratingsRef}
            >
              <span id="product-details__review__target" className="sui--ml-2">
                <RatingMeter
                  value={averageRating}
                  label={averageRating !== 0 ? `(${reviewData?.reviews?.TotalResults})` : ''}
                  RatingProps={{ color: 'brand' }}
                  data-testid="rating-button"
                />
              </span>
              {showBadge && <ArrowDown size="small" />}
            </a>
          )}
          {showBadge && channel === 'desktop' && (!reviewError) && reviewData?.reviews && ratingsRef.current && (
            <RatingsPopover
              open={showPopover}
              anchorEl={ratingsRef.current}
              onClick={handleRatingsClick}
              onClose={closePopover}
              onHoverEnter={cancelScheduledClose}
              onHoverLeave={scheduleClosePopover}
              reviewStats={reviewData?.reviews}
            />
          )}
          {showBadge && isQADataValid && ((hideWhenNoQA && qaData?.questionsAnswers?.TotalResults > 0) || !hideWhenNoQA) && (
            <Button
              onClick={handleQuestionAnswerClick}
              className={`${!hideRatings ? 'sui-ml-2' : 'sui-ml-0'} sui-cursor-pointer hover:sui-text-underline`}
              variant="ghost"
              href={qaselector || targetURL}
            >
              {qaData?.questionsAnswers?.TotalResults
                ? `Questions & Answers\xA0(${qaData?.questionsAnswers?.TotalResults})`
                : 'Questions & Answers\xA0'}
            </Button>
          )}
        </>
      )}
    </div>
  );
};

ProductDetailsRatings.propTypes = {
  offsetselector: string,
  pageType: string,
  qaselector: string,
  showBadge: bool,
  target: string,
  disableReviewsIfZero: bool,
  onClick: func,
  useHeaderIndex: bool,
  hideRatingsIfHDPPSku: bool,
  targetURL: string,
  hideRatings: bool,
  hideWhenNoQA: bool
};

ProductDetailsRatings.dataModel = dataModel;

export default ProductDetailsRatings;
